import { Capacitor } from '@capacitor/core';
import { Preferences } from '@capacitor/preferences';
import type { SupportedStorage } from '@supabase/supabase-js';

const SUPABASE_AUTH_KEY_PREFIX = 'sb-';
const MIGRATION_FLAG_KEY = 'fixnow:auth-storage-migrated';
const BRIDGE_PROBE_KEY = 'fixnow:cap-bridge-probe';

function isNativePreferencesStorage(): boolean {
  return Capacitor.isNativePlatform();
}

let bridgeReadyPromise: Promise<void> | null = null;

/** Capacitor WebView 剛啟動時 Preferences 可能尚未就緒，短暫重試 */
async function ensureNativeBridgeReady(): Promise<void> {
  if (!isNativePreferencesStorage()) return;
  if (bridgeReadyPromise) {
    await bridgeReadyPromise;
    return;
  }
  bridgeReadyPromise = (async () => {
    const deadline = Date.now() + 4000;
    while (Date.now() < deadline) {
      try {
        await Preferences.get({ key: BRIDGE_PROBE_KEY });
        return;
      } catch {
        await new Promise((r) => setTimeout(r, 40));
      }
    }
    console.warn('[supabaseAuthStorage] Capacitor Preferences bridge 等待逾時，仍繼續嘗試讀寫');
  })();
  await bridgeReadyPromise;
}

async function migrateLegacyLocalStorageToPreferences(): Promise<void> {
  if (!isNativePreferencesStorage()) return;

  try {
    await ensureNativeBridgeReady();
    const { value: migrated } = await Preferences.get({ key: MIGRATION_FLAG_KEY });
    if (migrated === '1') return;

    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (!key?.startsWith(SUPABASE_AUTH_KEY_PREFIX)) continue;
      const value = localStorage.getItem(key);
      if (value == null) continue;
      await Preferences.set({ key, value });
      localStorage.removeItem(key);
    }

    await Preferences.set({ key: MIGRATION_FLAG_KEY, value: '1' });
  } catch (error) {
    console.warn('[supabaseAuthStorage] 無法從 localStorage 遷移至 Preferences:', error);
  }
}

let migrationPromise: Promise<void> | null = null;

function ensureNativeMigration(): Promise<void> {
  if (!migrationPromise) {
    migrationPromise = migrateLegacyLocalStorageToPreferences();
  }
  return migrationPromise;
}

/** 在 React 掛載前呼叫（原生 App），避免 Supabase 早於 Capacitor 讀取 session */
export async function prepareSupabaseAuthStorage(): Promise<void> {
  if (!isNativePreferencesStorage()) return;
  await ensureNativeBridgeReady();
  await ensureNativeMigration();
}

/** Web 用 localStorage；Capacitor App 用 Preferences 持久化 Supabase session */
export const supabaseAuthStorage: SupportedStorage = {
  getItem: async (key: string) => {
    if (isNativePreferencesStorage()) {
      await ensureNativeMigration();
      await ensureNativeBridgeReady();
      const { value } = await Preferences.get({ key });
      return value ?? null;
    }
    return localStorage.getItem(key);
  },
  setItem: async (key: string, value: string) => {
    if (isNativePreferencesStorage()) {
      await ensureNativeBridgeReady();
      await Preferences.set({ key, value });
      try {
        localStorage.removeItem(key);
      } catch {
        /* ignore */
      }
      return;
    }
    localStorage.setItem(key, value);
  },
  removeItem: async (key: string) => {
    if (isNativePreferencesStorage()) {
      await ensureNativeBridgeReady();
      await Preferences.remove({ key });
    }
    try {
      localStorage.removeItem(key);
    } catch {
      /* ignore */
    }
  },
};
